import React, { useEffect, useMemo } from 'react'
import { Pagination } from '@/components/ds2'
import { DEFAULT_ROWS_PER_PAGE_OPTIONS } from '@/constants/rowsPerPage'

interface TablePaginationProps {
    page: number
    setPage: (page: number) => void
    rowsPerPage: number
    setRowsPerPage: (rowsPerPage: number) => void
    total: number
    hideRowsPerPage?: boolean
    rowsPerPageOptions?: number[]
    /** Disable the page controls (prev/next + page-size select) — e.g. while a fetch is in flight. */
    disabled?: boolean
    /** `ds2` renders the design-system-v2 footer (the ds2 `Pagination` `compact` variant). */
    variant?: 'default' | 'ds2'
}

interface TablePaginationModel {
    rowsPerPageOptions: number[]
    rowsPerPage: number
    totalPages: number
    page: number
    rangeLabel: string
    hideRowsPerPage: boolean
    disabled: boolean
    canGoPrevious: boolean
    canGoNext: boolean
    goToPreviousPage: () => void
    goToNextPage: () => void
    changeRowsPerPage: (value: number) => void
}

/** Clamping, option sanitizing, page reset and disabled handling — shared by every footer variant so only the markup differs. */
const useTablePaginationModel = ({
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    total,
    hideRowsPerPage = false,
    rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
    disabled = false,
}: TablePaginationProps): TablePaginationModel => {
    const sanitizedRowsPerPageOptions = useMemo(() => {
        const options = (rowsPerPageOptions.length > 0 ? rowsPerPageOptions : DEFAULT_ROWS_PER_PAGE_OPTIONS).filter(
            (opt, index, list) => Number.isFinite(opt) && opt > 0 && list.indexOf(opt) === index
        )
        return options.length > 0 ? options : DEFAULT_ROWS_PER_PAGE_OPTIONS
    }, [rowsPerPageOptions])

    const effectiveRowsPerPage = sanitizedRowsPerPageOptions.includes(rowsPerPage) ? rowsPerPage : sanitizedRowsPerPageOptions[0]

    useEffect(() => {
        if (rowsPerPage !== effectiveRowsPerPage) {
            setRowsPerPage(effectiveRowsPerPage)
            setPage(1)
        }
    }, [rowsPerPage, effectiveRowsPerPage, setRowsPerPage, setPage])

    const totalPages = Math.max(1, Math.ceil(total / effectiveRowsPerPage))
    const safePage = Math.min(Math.max(page, 1), totalPages)
    const from = total === 0 ? 0 : (safePage - 1) * effectiveRowsPerPage + 1
    const to = Math.min(safePage * effectiveRowsPerPage, total)

    return {
        rowsPerPageOptions: sanitizedRowsPerPageOptions,
        rowsPerPage: effectiveRowsPerPage,
        totalPages,
        page: safePage,
        rangeLabel: total === 0 ? '0 of 0' : `${from}–${to} of ${total}`,
        hideRowsPerPage,
        disabled,
        canGoPrevious: !disabled && safePage > 1,
        canGoNext: !disabled && safePage < totalPages,
        goToPreviousPage: () => setPage(safePage - 1),
        goToNextPage: () => setPage(safePage + 1),
        changeRowsPerPage: (value) => {
            // Reset to page 1 first, then change the size. Order matters for
            // adapters that bridge these two setters into a single merged
            // callback (e.g. ProfileTable): the size change must be applied
            // last so it is not clobbered by a stale page-only update.
            setPage(1)
            setRowsPerPage(value)
        },
    }
}

const DefaultTablePagination: React.FC<{ model: TablePaginationModel }> = ({ model }) => (
    <div className="flex items-center justify-between border-t border-slate-200 px-4 py-2.5 dark:border-slate-700">
        {model.hideRowsPerPage ? (
            <div />
        ) : (
            <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 dark:text-slate-400">Rows per page</span>
                <select
                    value={model.rowsPerPage}
                    disabled={model.disabled}
                    onChange={(e) => model.changeRowsPerPage(Number(e.target.value))}
                    className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 shadow-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600/20 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                >
                    {model.rowsPerPageOptions.map((opt) => (
                        <option key={opt} value={opt}>
                            {opt}
                        </option>
                    ))}
                </select>
            </div>
        )}

        <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500 dark:text-slate-400">{model.rangeLabel}</span>
            <div className="flex items-center gap-1">
                <button
                    onClick={model.goToPreviousPage}
                    disabled={!model.canGoPrevious}
                    aria-label="Previous page"
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md text-lg font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
                >
                    ‹
                </button>
                <span className="min-w-[2rem] text-center text-xs font-medium text-slate-700 dark:text-slate-200">
                    {model.page} / {model.totalPages}
                </span>
                <button
                    onClick={model.goToNextPage}
                    disabled={!model.canGoNext}
                    aria-label="Next page"
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md text-lg font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
                >
                    ›
                </button>
            </div>
        </div>
    </div>
)

/**
 * Shared pagination footer for DS tables.
 *
 * Lives in the DS so `ProfileTable` (and any future DS list component) can
 * render consistent pagination without importing from a feature-specific
 * `pages/dashboard/*` folder. The previous home at
 * `pages/dashboard/pages/shared-components/TablePagination.tsx` is now a
 * 1-line re-export shim that delegates to this module — keeping the ~9
 * existing call-sites working while we migrate imports gradually.
 *
 * The page/size logic lives in `useTablePaginationModel`; the `ds2` variant hands it to the ds2 `Pagination`.
 * The default variant is light-mode only for now; dark-mode parity is tracked as a separate follow-up
 * (grep this file for "dark:" to audit later).
 */
export const TablePagination: React.FC<TablePaginationProps> = ({ variant = 'default', ...props }) => {
    const model = useTablePaginationModel(props)
    if (variant === 'default') return <DefaultTablePagination model={model} />
    return (
        <div className="border-t border-border px-4 py-3">
            <Pagination
                variant="compact"
                totalItems={props.total}
                page={model.page}
                pageSize={model.rowsPerPage}
                pageSizeOptions={model.hideRowsPerPage ? undefined : model.rowsPerPageOptions}
                onPageChange={props.setPage}
                onPageSizeChange={model.changeRowsPerPage}
                disabled={model.disabled}
            />
        </div>
    )
}
